"use client";

/**
 * Scene 4 — Your Path.
 *
 * "Abstract journey becomes tangible": Niche → Plan → Brand → Day 9 → Day 365
 * as five chronologically-specific day-beats with real artifact preview
 * screenshots from shipped Niche v3 / Plan v2 / Brand v2.
 *
 * Spec source: research/landing-v2/09-wireframe-v2-part-b.md §6
 *              research/landing-v2/05-landing-concept-v2.md §4.5
 *              research/landing-v2/10-preview-assets.md
 *
 * Voice sequence per wireframe part B §6:
 *   Block 1 (Day 0)          — Seeker observer
 *   Block 2 (Day 0 + 20 min) — Elena co-builder
 *   Block 3 (Day 0 + 40 min) — Mira decisive
 *   Block 4 (Day 9)          — Destroyer honest bifurcation + Mira sensory
 *   Block 5 (Day 365)        — Mira sensory, anti-hype
 *
 * Layout alternation (desktop): text-right, text-left, text-right, text-left,
 * text-right — each block's `reverse` flag flips the 40/60 side.
 *
 * Motion:
 *   - Each block fades up on whileInView (margin -80px)
 *   - 150ms stagger between blocks via `delay` prop
 *   - Hover: preview tilts back toward flat and scales 1.02 with amber glow
 *   - Credits strip fades in after last block
 *   - Reduced-motion: all entrances → instant, hover tilts disabled
 *
 * a11y:
 *   - SceneHeader as h2 (aria-labelledby target)
 *   - each DayBeatBlock is an <article> with alt-text on image
 *   - credits strip has explicit kicker + supporting body
 *
 * Anti-patterns dodged:
 *   - No hardcoded image dimensions (all from PREVIEW_IMAGES registry)
 *   - No $ shown in credits (credit-opacity rule, feedback_credits_opacity.md)
 *   - No banned vocab
 *   - Day-N specificity everywhere (no "soon"/"eventually")
 */

import { SceneContainer } from "../shared/SceneContainer";
import { DayBeatBlock } from "../shared/DayBeatBlock";
import { useSceneView } from "../shared/useSceneView";
import { PREVIEW_IMAGES } from "../data/previewImages";
import { SceneHeader } from "@/components/shared-primitives/SceneHeader";
import { SceneHookHero } from "@/components/shared-primitives/SceneHookHero";
import type { ReactNode } from "react";

const BLOCK_STAGGER_MS = 150;
const MOBILE_BEAT_SURFACE =
  "max-md:rounded-2xl max-md:border max-md:border-white/10 max-md:bg-white/[0.035] max-md:p-5 max-md:ring-1 max-md:ring-inset max-md:ring-white/5";

export default function Scene4YourPath() {
  useSceneView({
    sceneId: "4",
    sceneName: "Your Path",
    elementId: "scene-4-path",
  });

  return (
    <SceneContainer
      id="scene-4-path"
      ariaLabel="Your path — day-by-day journey"
      minHeight="min-h-[150vh]"
    >
      <div className="mx-auto max-w-6xl px-4 py-12 md:px-6 md:py-16">
        {/* Dual-rail scene header */}
        <SceneHeader id="scene-4-path-title" number={3} title="Your Path" />

        {/* Scene hook — three-sentence projection, gradient-clip word-stagger.
            "— if you shipped" tail per wireframe §6.7 line 3 (honest
            bifurcation, Pillar 4). Do NOT drop. */}
        <SceneHookHero gradient className="mb-20 md:mb-28">
          Sunday, 11pm. You type one thing that bugs you. By Wednesday you
          have a plan, a name, a page. By day 9, someone&apos;s paying for it
          &mdash; if you shipped.
        </SceneHookHero>

        {/* Five day-beat blocks — alternating layout, staggered fade-up */}
        <div className="relative flex flex-col gap-12 md:gap-28">
          <div
            aria-hidden
            className="absolute bottom-8 left-[13px] top-3 hidden w-px bg-gradient-to-b from-amber-300/70 via-amber-300/25 to-emerald-300/40 max-md:block"
          />
          {/* Block 1 — DAY 0 · Seeker observer voice.
              Spec §6.2 block-1: 3 mini foil badges, NOT a preview image. */}
          <MobileBeatShell index={1}>
            <DayBeatBlock
              dayLabel="Day 0"
              badges={["A+", "B+", "A-"]}
              reverse={false}
              rotateDeg={-2}
              delay={0}
              className={MOBILE_BEAT_SURFACE}
            >
              <p>Half an hour. Four questions. Three survived niches.</p>
              <p className="mt-2 font-heading italic text-white/70">
                &mdash; Behind the curtain: agents sweep the open web, scan social
                signal, stress-test theories, hunt niches before they&apos;re named.
                Weeks of research, even with AI. Thirty minutes here.
              </p>
            </DayBeatBlock>
          </MobileBeatShell>

          {/* Block 2 — DAY 0 + 20 MIN · Elena co-builder voice */}
          <MobileBeatShell index={2}>
            <DayBeatBlock
              dayLabel="Day 0 + 20 min"
              preview={PREVIEW_IMAGES.planTour}
              previewAriaLabel="Plan tour: 5 frames"
              reverse
              rotateDeg={-1}
              delay={(BLOCK_STAGGER_MS * 1) / 1000}
              className={MOBILE_BEAT_SURFACE}
            >
              Plan generated. 8 scenes. 90-day blueprint. A multi-agent panel
              drafted, debated, and validated it before your coffee cooled.
            </DayBeatBlock>
          </MobileBeatShell>

          {/* Block 3 — DAY 0 + 40 MIN · Mira decisive voice.
              Spec §6.7: drop "Press release ready to paste" — not in spec. */}
          <MobileBeatShell index={3}>
            <DayBeatBlock
              dayLabel="Day 0 + 40 min"
              preview={PREVIEW_IMAGES.brandTour}
              previewAriaLabel="Brand tour: 5 frames"
              reverse={false}
              rotateDeg={-2}
              delay={(BLOCK_STAGGER_MS * 2) / 1000}
              className={MOBILE_BEAT_SURFACE}
            >
              Brand kit. Name, palette, lookbook, 20-page PDF.
            </DayBeatBlock>
          </MobileBeatShell>

          {/* Block 4 — DAY 9 · honest bifurcation + sensory close.
              Spec §6.2 block-4 variant: text-only. No preview image —
              Day 9 is the reader's outcome, not a Foundry artifact.
              Visual silence = honesty. */}
          <MobileBeatShell index={4}>
            <DayBeatBlock
              dayLabel="Day 9"
              reverse
              rotateDeg={-1}
              delay={(BLOCK_STAGGER_MS * 3) / 1000}
              className={MOBILE_BEAT_SURFACE}
            >
              <p>
                Ship. Ask five people. Tell one on Friday. Someone&apos;s paid you.
              </p>
              <p className="mt-2 font-heading italic text-white/70">
                &mdash; $340 on a Tuesday.
              </p>
            </DayBeatBlock>
          </MobileBeatShell>

          {/* Block 5 — DAY 365 · Mira italic-serif projection, present tense.
              Spec §6.2 block-5 variant: text-only sensory moment.
              No preview image — "no image would match without cheapening it." */}
          <MobileBeatShell index={5}>
            <DayBeatBlock
              dayLabel="Day 365"
              reverse={false}
              rotateDeg={-2}
              delay={(BLOCK_STAGGER_MS * 4) / 1000}
              className={MOBILE_BEAT_SURFACE}
            >
              <p className="font-heading text-[18px] font-bold italic leading-[1.35] text-white/75 md:text-[22px]">
                Wednesday morning. The dashboard ticks past another sale before
                your coffee cools.
                <br />
                $14k this month &mdash; fourth in a row.
                <br />
                Still feels like Tuesday morning.
              </p>
            </DayBeatBlock>
          </MobileBeatShell>
        </div>

      </div>
    </SceneContainer>
  );
}

function MobileBeatShell({
  index,
  children,
}: {
  index: number;
  children: ReactNode;
}) {
  return (
    <div className="relative md:contents">
      <span
        aria-hidden
        className="absolute left-0 top-4 z-10 hidden h-7 w-7 place-items-center rounded-full border border-amber-300/35 bg-surface text-[10px] font-bold tabular-nums text-amber-200 shadow-[0_0_18px_-10px_rgba(251,191,36,0.9)] max-md:grid"
      >
        {String(index).padStart(2, "0")}
      </span>
      <div className="max-md:pl-10 md:contents">{children}</div>
    </div>
  );
}
